import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  PlugZap, CheckCircle2, ShieldCheck, Loader2, ExternalLink, Trash2, KeyRound, MousePointerClick,
} from "lucide-react";

export default function Settings() {
  const qc = useQueryClient();
  const [pit, setPit] = useState("");
  const [locationId, setLocationId] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const { data: settings } = useQuery({ queryKey: ["ghl-settings"], queryFn: async () => (await api.get("/ghl/settings")).data });

  useEffect(() => { if (settings?.location_id) setLocationId(settings.location_id); }, [settings]);

  const save = async () => {
    if (!pit.trim() || !locationId.trim()) { toast.error("Enter both the token and Location ID"); return; }
    setSaving(true);
    try {
      await api.post("/ghl/settings", { pit, location_id: locationId });
      qc.invalidateQueries({ queryKey: ["ghl-settings"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      setPit("");
      toast.success("GoHighLevel connected");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/ghl/test");
      toast.success(data.message || "Connection OK");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Connection failed");
    } finally { setTesting(false); }
  };

  const disconnect = async () => {
    await api.delete("/ghl/settings");
    qc.invalidateQueries({ queryKey: ["ghl-settings"] });
    qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
    setLocationId(""); setPit("");
    toast.success("Disconnected");
  };

  return (
    <div data-testid="settings-page">
      <PageHeader title="GHL Connection" subtitle="Securely link your GoHighLevel sub-account to sync appointments, invoices and clients." />

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="rounded-2xl border-navy-900/5 bg-white p-8 shadow-ambient lg:col-span-2">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy-900"><PlugZap className="h-5 w-5 text-gold-400" /></div>
            <div>
              <h3 className="font-serif text-xl font-bold text-navy-900">Private Integration Token</h3>
              {settings?.connected ? (
                <span className="mt-1 flex items-center gap-1 text-sm text-emerald-600" data-testid="ghl-status-connected"><CheckCircle2 className="h-4 w-4" /> Connected to location {settings.location_id}</span>
              ) : (
                <span className="mt-1 text-sm text-navy-400" data-testid="ghl-status-disconnected">Not connected yet</span>
              )}
            </div>
          </div>

          <div className="mb-5 flex items-start gap-3 rounded-xl bg-gold-50 p-4">
            <MousePointerClick className="mt-0.5 h-5 w-5 shrink-0 text-gold-600" />
            <p className="text-sm text-navy-600">
              <b className="text-navy-900">One click.</b> Paste your token &amp; Location ID once and press <b>Save &amp; Connect</b> — your Appointments, Invoices and Clients tabs then fill automatically. This connection is for your admin dashboard only; your clients never see it.
            </p>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> Private Integration Token (PIT)</Label>
              <Input type="password" data-testid="ghl-pit-input" value={pit} onChange={(e) => setPit(e.target.value)} placeholder={settings?.connected ? "•••••••• (enter new token to replace)" : "pit-xxxxxxxx"} className="mt-1 font-mono focus-visible:ring-gold-500" />
            </div>
            <div>
              <Label>Location ID (Sub-account)</Label>
              <Input data-testid="ghl-location-input" value={locationId} onChange={(e) => setLocationId(e.target.value)} placeholder="e.g. ve9EPM428h8vShlRW1KT" className="mt-1 font-mono focus-visible:ring-gold-500" />
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <Button data-testid="save-ghl-button" onClick={save} disabled={saving} className="rounded-full bg-gold-500 px-6 text-white hover:bg-gold-600">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save & Connect
              </Button>
              {settings?.connected && (
                <>
                  <Button data-testid="test-ghl-button" onClick={test} disabled={testing} variant="outline" className="rounded-full border-navy-100">
                    {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Test Connection
                  </Button>
                  <Button data-testid="disconnect-ghl-button" onClick={disconnect} variant="outline" className="rounded-full border-navy-100 text-destructive hover:bg-destructive/10">
                    <Trash2 className="mr-2 h-4 w-4" /> Disconnect
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card>

        <div className="space-y-6">
          <Card className="rounded-2xl border-navy-900/5 bg-white p-6 shadow-ambient">
            <div className="flex items-center gap-2 text-navy-900"><ShieldCheck className="h-5 w-5 text-gold-600" /><h4 className="font-semibold">Private & secure</h4></div>
            <ul className="mt-2 space-y-1.5 text-sm text-navy-500">
              <li>• Token is <b>encrypted at rest</b> and never sent to the browser.</li>
              <li>• Client details are <b>masked</b> (first name + last initial, hidden email/phone).</li>
              <li>• We only <b>read</b> from GHL — nothing is created, edited or deleted.</li>
              <li>• Client portal activity is never tracked here.</li>
            </ul>
          </Card>

          <Card className="rounded-2xl border-navy-900/5 bg-ivory p-6 shadow-none">
            <h4 className="font-semibold text-navy-900">How to get your token</h4>
            <ol className="mt-3 space-y-2 text-sm text-navy-600">
              <li>1. In GHL: <b>Settings → Private Integrations</b></li>
              <li>2. Create integration, tick <b>read-only</b> scopes only: calendars, contacts, invoices, products, prices, medias, surveys</li>
              <li>3. Copy the token & your Location ID</li>
              <li>4. Paste both here and Save</li>
            </ol>
            <a href="https://help.gohighlevel.com/support/solutions/articles/155000003054-private-integrations-everything-you-need-to-know" target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-gold-600 hover:text-gold-700">
              GHL guide <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Card>
        </div>
      </div>
    </div>
  );
}
